import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createBlock as _createBlock, withCtx as _withCtx, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = {
  "data-test-panel": "",
  class: "flex flex-col"
}
const _hoisted_2 = {
  key: 0,
  "data-errors": "",
  role: "alert",
  class: "m-0 mb-6 list-none rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700"
}
const _hoisted_3 = { class: "rounded-lg border border-gray-200 bg-[#f9fafb]" }
const _hoisted_4 = { key: 1 }

import { computed, ref } from 'vue'

import WizardConfirmModal from '@/components/neue/wizard/WizardConfirmModal.vue'
import WizardSection from '@/components/neue/wizard/WizardSection.vue'
import { fetchAgentImpact, reloadAgents, setAgentLive, useAgentGoLive } from '@/composables/useAgentGoLive'
import { useConfirm } from '@/composables/useConfirm'
import { useToast } from '@/composables/useToast'
import { injectWizard } from '@/composables/useWizardContext'
import { AGENT_CARDS, newAgentPath } from '@/config/agentCards'
import { STEP_AGENT_TYPES, STORE_STEP_KEYS } from '@/config/agentWizardSteps'
import { agentTestTitle, goLiveLine, stepKeyForType } from '@/helpers/agentTestCopy'
import { agentRowKey, skippedRows } from '@/helpers/agentWizardSummary'
import { mapTypeIncludingInactive } from '@/store/mappers'
import AgentImpactModal from '@/view/ai/AgentImpactModal.vue'
import AgentTestConversationCard from '@/view/ai/agentWizard/AgentTestConversationCard.vue'
import AgentTestNumberSection from '@/view/ai/agentWizard/AgentTestNumberSection.vue'

const SKIPPED = 'Skipped'
const GO_LIVE_ALL_ERROR = 'Some agents could not go live. Try again.'


export default {
  __name: 'AgentTestPanel',
  setup(__props, { expose: __expose }) {

const wizard = injectWizard()
const goLive = useAgentGoLive()
const { state: confirmState, ask, confirm: confirmYes, cancel: confirmNo } = useConfirm()
const { showToast } = useToast()

const testContact = ref(wizard.status.value?.test_contact ?? null)
const hasNumber = computed(() => Boolean(testContact.value))
const liveTestAgentId = ref(null)

const isTesting = agent => Boolean(agent.attributes?.test_mode)

const agentsById = computed(() => new Map(mapTypeIncludingInactive('agent').map(agent => [String(agent.id), agent])))
const playbooksById = computed(
  () => new Map(mapTypeIncludingInactive('agent_playbook').map(playbook => [String(playbook.id), playbook]))
)

const playbookFor = agent => playbooksById.value.get(String(agent.relationships?.agent_playbook?.data?.id)) ?? null
const storeIdFor = playbook => {
  const id = playbook?.attributes?.belongs_to_id
  return id === null || id === undefined ? null : String(id)
}
const storeFor = storeId => wizard.stores.value.find(store => String(store.id) === storeId)

const cardFor = agent => {
  const playbook = playbookFor(agent)
  const storeId = storeIdFor(playbook)
  const store = storeId === null ? null : storeFor(storeId)
  const storeName = store && wizard.stores.value.length > 1 ? store.store_name : ''
  return {
    agent,
    playbook,
    storeId,
    storeName,
    storefrontUrl: (store ?? wizard.stores.value[0])?.storefront_url ?? '',
    title: agentTestTitle(agent.attributes?.type, storeName)
  }
}

const cards = computed(() =>
  wizard.createdAgentIds.value
    .map(id => agentsById.value.get(String(id)))
    .filter(Boolean)
    .map(cardFor)
)

const titleFor = agentId => {
  const card = cards.value.find(candidate => String(candidate.agent.id) === String(agentId))
  if (card) return card.title
  const agent = agentsById.value.get(String(agentId))
  return agent ? agentTestTitle(agent.attributes?.type) : ''
}

const otherLiveName = agentId =>
  liveTestAgentId.value && String(liveTestAgentId.value) !== String(agentId) ? titleFor(liveTestAgentId.value) : ''

// Rows the merchant skipped, minus the agents this setup created (after a reload they count as existing).
const createdKeys = computed(
  () =>
    new Set(
      cards.value.map(card => {
        const stepKey = stepKeyForType(card.agent.attributes?.type)
        return agentRowKey(stepKey, STORE_STEP_KEYS.includes(stepKey) ? card.storeId : null)
      })
    )
)
const slugFor = stepKey => AGENT_CARDS.find(card => card.agentType === STEP_AGENT_TYPES[stepKey])?.key
const setUpPath = row => {
  const [stepKey, storeId] = row.key.split(':')
  const slug = slugFor(stepKey)
  return row.reason === SKIPPED && slug ? newAgentPath(slug, storeId) : undefined
}
const skipped = computed(() =>
  skippedRows(wizard.data, wizard.stores.value, wizard.existing.value)
    .filter(row => !createdKeys.value.has(row.key))
    .map(row => ({ ...row, to: setUpPath(row) }))
)

const goLiveOne = agentId => goLive.request(agentId, agentsById.value.get(String(agentId))?.attributes?.type ?? '')

const goLiveCopy = async testing => {
  const estimates = await Promise.all(testing.map(card => fetchAgentImpact(card.agent.id)))
  return testing.map((card, i) => goLiveLine(card.title, estimates[i], card.agent.attributes?.type))
}

const putLive = async testing => {
  for (const card of testing) await setAgentLive(card.agent.id)
}

// Every created agent still in testing goes live, one PUT at a time, after one confirmation.
const goLiveAll = async () => {
  const testing = cards.value.filter(card => isTesting(card.agent))
  if (!testing.length) return false
  const lines = await goLiveCopy(testing)
  const count = testing.length
  const sure = await ask({
    title: `Go live with ${count} ${count === 1 ? 'agent' : 'agents'}?`,
    message: lines,
    confirmLabel: 'Go live'
  })
  if (!sure) return false
  try {
    await putLive(testing)
    showToast('AI agents are live')
    return true
  } catch {
    showToast(GO_LIVE_ALL_ERROR, { type: 'error' })
    return false
  } finally {
    await reloadAgents().catch(() => {})
  }
}

__expose({ goLiveAll })

return (_ctx, _cache) => {
  const _component_router_link = _resolveComponent("router-link")

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_unref(wizard).errors.value.length)
      ? (_openBlock(), _createElementBlock("ul", _hoisted_2, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(wizard).errors.value, (error) => {
            return (_openBlock(), _createElementBlock("li", { key: error }, _toDisplayString(error), 1))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    _createVNode(AgentTestNumberSection, {
      "test-contact": testContact.value,
      "suggested-first-name": _unref(wizard).status.value?.suggested_first_name ?? '',
      onSaved: _cache[0] || (_cache[0] = $event => (testContact.value = $event))
    }, null, 8, ["test-contact", "suggested-first-name"]),
    _createVNode(WizardSection, {
      title: "Test each agent",
      description: "Text yourself from each agent and reply from your phone. It answers you the way it would answer a customer.",
      plain: ""
    }, {
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cards.value, (card) => {
          return (_openBlock(), _createBlock(AgentTestConversationCard, {
            key: card.agent.id,
            agent: card.agent,
            playbook: card.playbook,
            "store-name": card.storeName,
            "storefront-url": card.storefrontUrl,
            "has-number": hasNumber.value,
            "live-test-agent-name": otherLiveName(card.agent.id),
            onGoLive: goLiveOne,
            onLiveTest: _cache[1] || (_cache[1] = $event => (liveTestAgentId.value = $event))
          }, null, 8, ["agent", "playbook", "store-name", "storefront-url", "has-number", "live-test-agent-name"]))
        }), 128))
      ]),
      _: 1
    }),
    (skipped.value.length)
      ? (_openBlock(), _createBlock(WizardSection, {
          key: 1,
          title: "Skipped",
          plain: ""
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_3, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(skipped.value, (row, i) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: row.key,
                  "data-skipped-row": "",
                  class: _normalizeClass(["flex items-center justify-between gap-3 px-3.5 py-2.5 text-sm text-gray-500", i ? 'border-t border-gray-200' : ''])
                }, [
                  _createElementVNode("span", null, _toDisplayString(row.label), 1),
                  (row.to)
                    ? (_openBlock(), _createBlock(_component_router_link, {
                        key: 0,
                        to: row.to,
                        class: "text-[13px] text-gray-600 underline hover:text-gray-900"
                      }, {
                        default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                          _createTextVNode(" Set up ", -1)
                        ]))]),
                        _: 1
                      }, 8, ["to"]))
                    : (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(row.reason), 1))
                ], 2))
              }), 128))
            ])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(AgentImpactModal, {
      "is-open": _unref(goLive).impactOpen.value,
      saving: _unref(goLive).saving.value,
      "agent-type": _unref(goLive).agentType.value,
      estimate: _unref(goLive).estimate.value,
      onConfirm: _unref(goLive).confirm,
      onCancel: _unref(goLive).cancel
    }, null, 8, ["is-open", "saving", "agent-type", "estimate", "onConfirm", "onCancel"]),
    _createVNode(WizardConfirmModal, _mergeProps(_unref(confirmState), {
      onConfirm: _unref(confirmYes),
      onCancel: _unref(confirmNo)
    }), null, 16, ["onConfirm", "onCancel"])
  ]))
}
}

}